import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { aiToast } from './shared/ai-toast';

import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';

interface ClaudeStatus {
  configured: boolean;
  version?: string;
  auth_ok?: boolean;
}

interface AgentStatus {
  teams: { configured: boolean };
  graph: { configured: boolean };
  outreach_running: boolean;
}

interface ClaudeTestResult {
  ok: boolean;
  latency_ms?: number;
  error?: string;
}

type ClaudeAuthPhase =
  | 'starting'
  | 'awaiting_browser'
  | 'awaiting_code'
  | 'success'
  | 'failed'
  | 'expired';

interface ClaudeAuthStartResult {
  sessionId: string;
  status: ClaudeAuthPhase;
  loginUrl?: string;
  needsCode?: boolean;
  error?: string;
}

interface ClaudeAuthStatusResult {
  sessionId: string;
  status: ClaudeAuthPhase;
  loginUrl?: string;
  needsCode?: boolean;
  error?: string;
}

interface ClaudeAuthCodeResult {
  ok: boolean;
  status?: ClaudeAuthPhase;
  authorized?: boolean;
  error?: string;
}

function CheckTab() {
  const [claudeStatus, setClaudeStatus] = useState<ClaudeStatus | null>(null);
  const [agentStatus, setAgentStatus] = useState<AgentStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [testingClaude, setTestingClaude] = useState(false);
  const [claudeTestResult, setClaudeTestResult] = useState<ClaudeTestResult | null>(
    null,
  );
  const [authSessionId, setAuthSessionId] = useState<string | null>(null);
  const [authLoginUrl, setAuthLoginUrl] = useState<string | null>(null);
  const [authNeedsCode, setAuthNeedsCode] = useState(false);
  const [authCode, setAuthCode] = useState('');
  const [authInProgress, setAuthInProgress] = useState(false);
  const [authResult, setAuthResult] = useState<string | null>(null);
  const [submittingAuthCode, setSubmittingAuthCode] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testMessageResult, setTestMessageResult] = useState<string | null>(
    null,
  );
  const [graphTestQuery, setGraphTestQuery] = useState('');
  const [testingGraph, setTestingGraph] = useState(false);
  const [graphTestResult, setGraphTestResult] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [claude, agent] = await Promise.all([
        fetchWrapper.get(urls.DevOps.aiClaudeStatus),
        fetchWrapper.get(urls.DevOps.agentStatus),
      ]);
      setClaudeStatus(claude);
      setAgentStatus(agent);
    } catch {
      aiToast.error('Failed to load integration status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  useEffect(() => {
    if (!authSessionId || !authInProgress) return undefined;

    let cancelled = false;
    const poll = async () => {
      try {
        const status: ClaudeAuthStatusResult = await fetchWrapper.get(
          urls.DevOps.aiClaudeAuthStatus(authSessionId),
        );
        if (cancelled) return;

        if (status.loginUrl) {
          setAuthLoginUrl(status.loginUrl);
        }
        setAuthNeedsCode(Boolean(status.needsCode));

        if (status.status === 'success') {
          setAuthInProgress(false);
          setAuthResult('Claude login succeeded');
          aiToast.success('Claude login succeeded');
          await load();
          return;
        }
        if (status.status === 'failed' || status.status === 'expired') {
          setAuthInProgress(false);
          const message = status.error ?? 'Claude login failed';
          setAuthResult(message);
          aiToast.error(message);
        }
      } catch (error: any) {
        if (cancelled) return;
        const notFound =
          error?.response?.status === 404 ||
          error?.message?.includes('404') ||
          error?.message?.includes('not found');
        if (notFound) {
          try {
            const claude = await fetchWrapper.get(urls.DevOps.aiClaudeStatus);
            if (claude?.auth_ok) {
              setAuthInProgress(false);
              setAuthResult('Claude login succeeded');
              aiToast.success('Claude login succeeded');
              setClaudeStatus(claude);
              return;
            }
          } catch {
            // fall through
          }
        }
        setAuthInProgress(false);
        setAuthResult('Failed to check login status');
        aiToast.error('Failed to check login status');
      }
    };

    const intervalId = window.setInterval(() => {
      poll().catch(() => undefined);
    }, 2000);
    poll().catch(() => undefined);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [authInProgress, authSessionId, load]);

  const startClaudeAuth = async () => {
    setAuthInProgress(true);
    setAuthResult(null);
    setAuthLoginUrl(null);
    setAuthNeedsCode(false);
    setAuthCode('');
    setAuthSessionId(null);

    try {
      const result: ClaudeAuthStartResult = await fetchWrapper.post(
        urls.DevOps.aiClaudeAuthStart,
        {},
      );
      setAuthSessionId(result.sessionId);

      if (result.status === 'success') {
        setAuthInProgress(false);
        setAuthResult('Claude is already authenticated');
        aiToast.success('Claude is already authenticated');
        await load();
        return;
      }

      if (result.loginUrl) {
        setAuthLoginUrl(result.loginUrl);
      }
      setAuthNeedsCode(Boolean(result.needsCode));

      if (result.status === 'failed') {
        setAuthInProgress(false);
        const message = result.error ?? 'Failed to start Claude login';
        setAuthResult(message);
        aiToast.error(message);
      }
    } catch (error: any) {
      setAuthInProgress(false);
      const message = error?.message ?? 'Failed to start Claude login';
      setAuthResult(message);
      aiToast.error(message);
    }
  };

  const submitAuthCode = async () => {
    const code = authCode.trim();
    if (!code || !authSessionId) {
      aiToast.error('Enter the login code from Claude');
      return;
    }
    setSubmittingAuthCode(true);
    try {
      const result: ClaudeAuthCodeResult = await fetchWrapper.post(
        urls.DevOps.aiClaudeAuthCode,
        {
          sessionId: authSessionId,
          code,
        },
      );
      if (result.authorized || result.status === 'success') {
        setAuthInProgress(false);
        setAuthResult('Claude login succeeded');
        aiToast.success('Claude login succeeded');
        await load();
      } else {
        aiToast.success('Code submitted — finishing login…');
      }
    } catch (error: any) {
      aiToast.error(error?.message ?? 'Failed to submit login code');
    } finally {
      setSubmittingAuthCode(false);
    }
  };

  const testClaude = async () => {
    setTestingClaude(true);
    setClaudeTestResult(null);
    try {
      const result: ClaudeTestResult = await fetchWrapper.post(
        urls.DevOps.aiClaudeTest,
        {},
      );
      setClaudeTestResult(result);
      if (result.ok) {
        aiToast.success(
          `Claude OK${result.latency_ms != null ? ` (${result.latency_ms} ms)` : ''}`,
        );
      } else {
        aiToast.error(result.error ?? 'Claude test failed');
      }
    } catch (error: any) {
      const message = error?.message ?? 'Claude test failed';
      setClaudeTestResult({ ok: false, error: message });
      aiToast.error(message);
    } finally {
      setTestingClaude(false);
    }
  };

  const sendTestMessage = async () => {
    const email = testEmail.trim();
    if (!email) {
      aiToast.error('Enter a recipient email');
      return;
    }
    setSendingTest(true);
    setTestMessageResult(null);
    try {
      const res = await fetchWrapper.post(urls.DevOps.agentTestMessage, {
        email,
      });
      if (res?.delivered) {
        setTestMessageResult('Delivered');
        aiToast.success('Test Teams message delivered');
      } else {
        const reason = res?.reason ?? 'Test message was not delivered';
        setTestMessageResult(reason);
        aiToast.error(reason);
      }
    } catch (error: any) {
      const reason =
        error?.response?.data?.reason ??
        error?.message ??
        'Failed to send test message';
      setTestMessageResult(reason);
      aiToast.error(reason);
    } finally {
      setSendingTest(false);
    }
  };

  const testGraphSearch = async () => {
    setTestingGraph(true);
    setGraphTestResult(null);
    try {
      const res = await fetchWrapper.post(urls.DevOps.agentTestGraph, {
        query: graphTestQuery.trim() || undefined,
      });
      if (res?.ok) {
        const count = res.users?.length ?? 0;
        const sample = (res.users ?? [])
          .slice(0, 3)
          .map(
            (u: { displayName: string | null; mail: string | null }) =>
              u.displayName ?? u.mail ?? '—',
          )
          .join(', ');
        const msg =
          count > 0
            ? `OK — ${count} user(s) matched${sample ? `: ${sample}` : ''}`
            : 'OK — directory reachable (no users matched)';
        setGraphTestResult(msg);
        aiToast.success(msg);
      } else {
        const reason = res?.reason ?? 'Graph test failed';
        setGraphTestResult(reason);
        aiToast.error(reason);
      }
    } catch (error: any) {
      const reason =
        error?.response?.data?.reason ??
        error?.message ??
        'Failed to test Graph';
      setGraphTestResult(reason);
      aiToast.error(reason);
    } finally {
      setTestingGraph(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6">Integration check</Typography>
        <Typography variant="body2" color="text.secondary">
          Verify Claude CLI, Teams bot and Graph connectivity. Model selection
          for runs is configured on the Optimization and Bot tabs.
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Claude CLI
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Chip
            size="small"
            label={
              claudeStatus?.configured
                ? 'Claude: configured'
                : 'Claude: not configured'
            }
            color={claudeStatus?.configured ? 'success' : 'default'}
            data-testid="check-claude-status"
          />
          {claudeStatus?.version && (
            <Chip size="small" label={`v${claudeStatus.version}`} />
          )}
          {claudeStatus?.auth_ok != null && (
            <Chip
              size="small"
              label={claudeStatus.auth_ok ? 'Auth OK' : 'Auth failed'}
              color={claudeStatus.auth_ok ? 'success' : 'error'}
            />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          On Azure App Service, set persistent storage and{' '}
          <code>CLAUDE_CONFIG_DIR</code> so credentials survive restarts.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={testClaude}
            disabled={testingClaude || authInProgress}
            data-testid="check-claude-test">
            {testingClaude ? 'Testing…' : 'Test Claude'}
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={startClaudeAuth}
            disabled={authInProgress || !claudeStatus?.configured}
            data-testid="check-claude-auth">
            {authInProgress ? 'Signing in…' : 'Claude Auth'}
          </Button>
          {authLoginUrl && authInProgress && (
            <Button
              variant="contained"
              color="secondary"
              size="small"
              href={authLoginUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="check-claude-auth-open">
              Open login page
            </Button>
          )}
        </Box>
        {authLoginUrl && authInProgress && (
          <Alert severity="info" sx={{ mb: 2 }} data-testid="check-claude-auth-hint">
            Click <strong>Open login page</strong> to sign in with Claude. After
            sign-in, paste the login code below if prompted.
          </Alert>
        )}
        {authNeedsCode && authInProgress && (
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Login code"
                placeholder="Paste code shown after sign-in"
                value={authCode}
                onChange={(e) => setAuthCode(e.target.value)}
                disabled={submittingAuthCode}
                data-testid="check-claude-auth-code"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                variant="outlined"
                size="small"
                onClick={submitAuthCode}
                disabled={submittingAuthCode || !authCode.trim()}
                data-testid="check-claude-auth-code-submit">
                {submittingAuthCode ? 'Submitting…' : 'Submit code'}
              </Button>
            </Grid>
          </Grid>
        )}
        {authResult && (
          <Alert
            severity={authResult.includes('succeeded') || authResult.includes('authenticated') ? 'success' : 'error'}
            sx={{ mb: 1 }}
            data-testid="check-claude-auth-result">
            {authResult}
          </Alert>
        )}
        {claudeTestResult && (
          <Alert
            severity={claudeTestResult.ok ? 'success' : 'error'}
            sx={{ mt: 1 }}
            data-testid="check-claude-test-result">
            {claudeTestResult.ok
              ? `Probe succeeded${claudeTestResult.latency_ms != null ? ` in ${claudeTestResult.latency_ms} ms` : ''}`
              : claudeTestResult.error ?? 'Test failed'}
          </Alert>
        )}
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Microsoft Teams
        </Typography>
        <Chip
          size="small"
          label={
            agentStatus?.teams.configured
              ? 'Teams bot: configured'
              : 'Teams bot: not configured'
          }
          color={agentStatus?.teams.configured ? 'success' : 'default'}
          sx={{ mb: 2 }}
          data-testid="check-teams-status"
        />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Any email address — the recipient does not need a CloudCost account.
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Recipient email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              disabled={!agentStatus?.teams.configured}
              data-testid="agent-test-message-email"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button
              variant="outlined"
              onClick={sendTestMessage}
              disabled={
                sendingTest || !testEmail.trim() || !agentStatus?.teams.configured
              }
              data-testid="agent-test-message-button">
              {sendingTest ? 'Sending…' : 'Send test message'}
            </Button>
          </Grid>
        </Grid>
        {testMessageResult && (
          <Alert
            severity={testMessageResult === 'Delivered' ? 'success' : 'warning'}
            sx={{ mt: 1 }}
            data-testid="check-teams-test-result">
            {testMessageResult}
          </Alert>
        )}
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Microsoft Graph (Entra)
        </Typography>
        <Chip
          size="small"
          label={
            agentStatus?.graph.configured
              ? 'Graph: configured'
              : 'Graph: not configured'
          }
          color={agentStatus?.graph.configured ? 'success' : 'default'}
          sx={{ mb: 2 }}
          data-testid="check-graph-status"
        />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Verifies directory access. Leave the query empty to list a few users,
          or enter a name prefix or email to search.
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Search query (optional)"
              placeholder="Name prefix or email"
              value={graphTestQuery}
              onChange={(e) => setGraphTestQuery(e.target.value)}
              disabled={!agentStatus?.graph.configured}
              data-testid="agent-test-graph-query"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button
              variant="outlined"
              onClick={testGraphSearch}
              disabled={testingGraph || !agentStatus?.graph.configured}
              data-testid="agent-test-graph-button">
              {testingGraph ? 'Testing…' : 'Test Graph search'}
            </Button>
          </Grid>
        </Grid>
        {graphTestResult && (
          <Alert
            severity={graphTestResult.startsWith('OK') ? 'success' : 'error'}
            sx={{ mt: 1 }}
            data-testid="check-graph-test-result">
            {graphTestResult}
          </Alert>
        )}
      </Grid>
    </Grid>
  );
}

export default CheckTab;
